import { createSignal, Show } from "solid-js";
import type { Accessor } from "solid-js";
import styles from "../../pages/Inventory.module.css";
import { useInventoryApi, type MoveRequest, type StorageInfo } from "../../services/inventoryApi";
import type { ItemDef } from "../../services/craftingResolver";
import { useItemName } from '../../hooks/useItemName';
import { useTranslation } from "../../hooks/useTranslation";
import Text from "../Text";
import StorageSelect from "../StorageSelect";

interface Props {
  item: ItemDef;
  sourceStorageId: Accessor<string | null>;
  availableQty: Accessor<number>;
  storages: Accessor<StorageInfo[]>;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
  onMoveCreated?: (req: MoveRequest) => void;
}

export default function MoveItemsSection(props: Props) {
  const { t } = useTranslation();
  const { itemName } = useItemName();
  const inv = useInventoryApi();
  const [expanded, setExpanded] = createSignal(false);
  const [destId, setDestId] = createSignal("");
  const [qty, setQty] = createSignal("");
  const [loading, setLoading] = createSignal(false);
  const [lastStatus, setLastStatus] = createSignal<string | null>(null);

  const otherStorages = () =>
    props.storages().filter(s => s.id !== props.sourceStorageId());

  async function doMove() {
    const sid = props.sourceStorageId();
    const did = destId();
    const q = parseInt(qty());
    const avail = props.availableQty();
    if (!sid || !did || isNaN(q) || q <= 0) return;
    if (avail <= 0) {
      props.onError(t("INVENTORY_MOVE_NONE_AVAILABLE"));
      return;
    }
    if (q > avail) {
      props.onError(t("INVENTORY_MOVE_ONLY_AVAILABLE", avail));
      return;
    }

    setLoading(true);
    setLastStatus(null);
    try {
      const displayName = itemName(props.item.key);
      const [req] = await inv.createMoveRequest(sid, did, [{ itemKey: props.item.key, quantity: q, itemDisplayName: displayName }]);
      setLastStatus(t("INVENTORY_MOVE_QUEUED", req.status));
      setQty("");
      props.onSuccess(t("INVENTORY_MOVE_CREATED", q, displayName));
      props.onMoveCreated?.(req);
    } catch (e: any) {
      props.onError(e?.message ?? t("INVENTORY_MOVE_FAILED"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div class={styles.detailSection}>
      <div
        class={styles.detailSectionTitle}
        style="cursor:pointer;display:flex;align-items:center;justify-content:space-between"
        onClick={() => setExpanded(e => !e)}
      >
        <span>
          <i class="fa-solid fa-truck" style="margin-right:6px" />
          <Text val="INVENTORY_MOVE_TO_STORAGE" />
        </span>
        <i class={`fa-solid fa-chevron-${expanded() ? "up" : "down"}`} style="font-size:10px" />
      </div>

      <Show when={expanded()}>
        <Show when={props.availableQty() <= 0}>
          <div class={styles.detailLabel} style="padding:4px 0;color:var(--warning-500);font-style:italic">
            <Text val="INVENTORY_MOVE_ALL_RESERVED" />
          </div>
        </Show>
        <div style={{ "margin-bottom": "var(--spacing-sm)" }}>
          <StorageSelect
            storages={otherStorages()}
            value={destId()}
            onChange={setDestId}
            placeholder={t("INVENTORY_MOVE_SELECT_DEST")}
            disabled={loading()}
          />
        </div>

        <div class={styles.detailRow}>
          <span class={styles.detailLabel}><Text val="QUANTITY" /></span>
          <input
            type="number"
            class={styles.priceInput}
            value={qty()}
            placeholder={t("INVENTORY_MOVE_MAX_PLACEHOLDER", props.availableQty())}
            min={1}
            max={props.availableQty()}
            onInput={e => setQty(e.currentTarget.value)}
            disabled={loading()}
          />
        </div>

        <Show when={lastStatus()}>
          <div class={styles.detailLabel} style="padding:4px 0;opacity:0.7;font-style:italic">
            {lastStatus()}
          </div>
        </Show>

        <button
          class={styles.moveCraftBtn}
          onClick={doMove}
          disabled={loading() || !destId() || !(parseInt(qty()) > 0) || props.availableQty() <= 0 || parseInt(qty()) > props.availableQty()}
          style="margin-top:6px;width:100%"
        >
          <Show when={loading()}>
            <i class="fa-solid fa-spinner fa-spin" style="margin-right:6px" />
          </Show>
          <Text val="INVENTORY_MOVE_ITEMS" />
        </button>
      </Show>
    </div>
  );
}
